<template>
  <ExternalQueryList
      headline="Leads"
      endpoint="/api/v2/project-leads"
      :endpoint-filters="{project_id: project.id}"
      :headlines="headlines"
      :checkbox-column-width="2"
      :button-column-width="2"
      :can-update="() => true"
      :can-destroy="() => true"
      :filters="filters"
      @show="projectLead => edit(projectLead)"
  >
    <template v-slot:default="{ row: projectLead }">
      <td>
        <div style="display: flex; justify-content: space-between">
          <div>
            <router-link :to="'/companies/' + projectLead.attributes.company_id">
              {{ projectLead.attributes.company_name }}
            </router-link>
          </div>

          <div style="white-space:nowrap;">
            <i
                v-if="projectLead.attributes.company_has_active_partnership"
                class="fa fa-book-reader"
                v-tooltip="'Active partnership'"
            />

            <i
                v-else-if="projectLead.attributes.company_has_partnership"
                class="fa fa-book-reader"
                style="opacity: 0.2"
                v-tooltip="'Expired partnership'"
            />

            <i v-else class="fa fa-book-reader" style="opacity: 0"/>
          </div>
        </div>
      </td>

      <td class="table__ellipsis">
        <span v-if="projectLead.attributes.status === 'new'" class="label label-warning">
          New
        </span>

        <span v-if="projectLead.attributes.status === 'active'" class="label label-info">
          Active
        </span>

        <span v-if="projectLead.attributes.status === 'declined'" class="label label-danger">
          Declined
        </span>

        <span v-if="projectLead.attributes.status === 'sale'" class="label label-success">
          Sale
        </span>
      </td>

      <td>
        <Avatar v-if="projectLead.attributes.portal_company_user_id" :color="projectLead.attributes.portal_company_user_color">
          {{ projectLead.attributes.portal_company_user_initials }}
        </Avatar>
      </td>

      <td>
        <Avatar v-if="projectLead.attributes.user_id" :color="projectLead.attributes.user_color">
          {{ projectLead.attributes.user_initials }}
        </Avatar>
      </td>
    </template>

    <template v-slot:list-actions="{canUpdateSelected}">
      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#copy-project-leads-modal" data-toggle="modal">
          <i class="fa fa-copy"></i> Copy Leads
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#add-company-tags-modal" data-toggle="modal">
          <i class="fa fa-plus"></i> Add Tags to Companies
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#update-project-lead-user-modal" data-toggle="modal">
          <i class="fa fa-user"></i> Update Assignee
        </a>
      </li>
    </template>

    <template v-slot:modals="{ selected, update, load }">
      <CreateModal :project="project" :users="users" @created="load"/>

      <EditModal :project-lead="editing" :users="users" @updated="update"/>

      <AddCompanyTagsModal
          :selected="selected"
          @updated="load"
      />

      <UpdateProjectLeadUserModal
          :selected="selected"
          :users="users"
          @updated="load"
      />

      <CopyProjectLeadsModal :selected="selected" @saved="load"/>
    </template>

    <template v-slot:headline-right>
      <a href="#create-project-lead-modal" data-toggle="modal" class="btn btn-white btn-sm" style="margin-top: 4px;">
        <i class="fa fa-plus"></i> Create
      </a>
    </template>
  </ExternalQueryList>
</template>

<script>
import CreateModal from "../Modals/Create.vue";
import EditModal from "../Modals/Edit.vue";
import ExternalQueryList from '../../../Panels/Lists/ExternalQuery'
import Avatar from "../../../Avatar.vue";
import UpdatePortalUserModal from "../../../Modals/Companies/UpdatePortalUserModal.vue";
import RemoveCompanyTagsModal from "../../../Modals/Companies/RemoveCompanyTagsModal.vue";
import AddCompanyTagsModal from "../../../Modals/Relations/Projects/AddCompanyTagsModal.vue";
import CreateProjectLeadsModal from "../../../Modals/Companies/CreateProjectLeadsModal.vue";
import DeletePortalUserModal from "../../../Modals/Companies/DeletePortalUserModal.vue";
import UpdateProjectLeadUserModal from "../../../Modals/Relations/Projects/UpdateProjectLeadUserModal.vue";
import CopyProjectLeadsModal from "../../../Modals/Relations/Projects/CopyProjectLeadsModal.vue";

export default {
  components: {CopyProjectLeadsModal,UpdateProjectLeadUserModal, DeletePortalUserModal, CreateProjectLeadsModal, AddCompanyTagsModal, RemoveCompanyTagsModal, UpdatePortalUserModal, Avatar, CreateModal,EditModal, ExternalQueryList},

  props: {
    project: {
      required: true
    },

    users: {
      required: true
    }
  },

  data() {
    return {
      editing: null
    }
  },

  computed: {
    headlines() {
      return [
        {width: 10, text: 'Company'},
        {width: 4, text: 'Status'},
        {width: 3, text: 'Responsible',sort_column: 'portal_company_user_initials'},
        {width: 3, text: 'Assignee', sort_column:'user_initials'},
      ]
    },

    filters() {
      return [
        [
          {
            width: 4,
            widget: 'text',
            name: 'search',
            placeholder: 'Search...'
          },
          {
            width: 4,
            widget: 'select',
            name: 'partnership_status',
            options: [
              {value: 'active', label: 'Active'},
              {value: 'expired', label: 'Expired'},
              {value: 'none', label: 'None'}
            ],
            nullable: true,
            placeholder: 'Choose Partnership Status...'
          },
          {
            width: 4,
            widget: 'select',
            name: 'status',
            options: [{value: 'new', label: 'New'}, {value: 'active', label: 'Active'}, {value: 'declined', label: 'Declined'}, {value: 'sale', label: 'Sale'}],
            nullable: true,
            placeholder: 'Choose Status...'
          },
          {
            width: 4,
            widget: 'select',
            name: 'portal_company_user_id',
            options: this.users.map(user => ({value: user.id, label: user.attributes.full_name})),
            nullable: true,
            placeholder: '(all responsibles)'
          },
          {
            width: 4,
            widget: 'select',
            name: 'user_id',
            options: this.users.map(user => ({value: user.id, label: user.attributes.full_name})),
            nullable: true,
            placeholder: '(all assignees)'
          },
          {
            width: 4,
            widget: 'submit'
          }
        ]
      ]
    }
  },

  methods: {
    edit(projectLead) {
      this.editing = projectLead

      $("#edit-project-lead-modal").modal("show")
    }
  }
}
</script>
